import * as THREE from "three";
import type { Player } from "../player/player";
import type { Arsenal } from "../combat/weapons";
import type { Mission } from "../game/mission";
import type { LevelData } from "../world/level";
import type { Director } from "../ai/enemy";
import { hitscan } from "../engine/collision";

export class HUD {
  el: HTMLElement;
  private tape: HTMLElement;
  private heading: HTMLElement;
  private obj: HTMLElement;
  private objSub: HTMLElement;
  private mag: HTMLElement;
  private res: HTMLElement;
  private wname: HTMLElement;
  private mode: HTMLElement;
  private hp: HTMLElement;
  private hpFill: HTMLElement;
  private arFill: HTMLElement;
  private stance: HTMLElement;
  private cross: HTMLElement;
  private hitmarker: HTMLElement;
  private hitFlash: HTMLElement;
  private killfeed: HTMLElement;
  private interact: HTMLElement;
  private interactLabel: HTMLElement;
  private toast: HTMLElement;
  private mapc: HTMLCanvasElement;
  private dmg: Record<string, HTMLElement>;
  private hint: HTMLElement;
  private threats: HTMLElement;
  private hmT = 0;
  private flashT = 0;
  private proj = new THREE.Vector3();

  constructor(root: HTMLElement) {
    this.el = root.querySelector("#hud")!;
    this.tape = root.querySelector("#compass-tape")!;
    this.heading = root.querySelector("#compass-heading")!;
    this.obj = root.querySelector("#obj-text")!;
    this.objSub = root.querySelector("#obj-sub")!;
    this.mag = root.querySelector("#ammo-mag")!;
    this.res = root.querySelector("#ammo-res")!;
    this.wname = root.querySelector("#weapon-name")!;
    this.mode = root.querySelector("#firemode")!;
    this.hp = root.querySelector("#hp-v")!;
    this.hpFill = root.querySelector("#hp-fill")!;
    this.arFill = root.querySelector("#ar-fill")!;
    this.stance = root.querySelector("#stance")!;
    this.cross = root.querySelector("#crosshair")!;
    this.hitmarker = root.querySelector("#hitmarker")!;
    this.hitFlash = root.querySelector("#hit-flash")!;
    this.killfeed = root.querySelector("#killfeed")!;
    this.interact = root.querySelector("#interact")!;
    this.interactLabel = root.querySelector("#interact-label")!;
    this.toast = root.querySelector("#toast")!;
    this.mapc = root.querySelector("#mapc")!;
    this.hint = root.querySelector("#hint")!;
    this.threats = root.querySelector("#threats")!;
    this.hint.style.opacity = "1";
    setTimeout(() => {
      this.hint.style.transition = "opacity 800ms";
      this.hint.style.opacity = "0";
    }, 12000);
    this.dmg = {
      n: root.querySelector("#dmg .n") as HTMLElement,
      e: root.querySelector("#dmg .e") as HTMLElement,
      s: root.querySelector("#dmg .s") as HTMLElement,
      w: root.querySelector("#dmg .w") as HTMLElement,
    };
    }

  show(v: boolean) {
    this.el.classList.toggle("hidden", !v);
  }

  hit(head: boolean) {
    this.hitmarker.classList.add("on");
    this.hitmarker.classList.toggle("head", head);
    this.hmT = 0.14;
    this.hitFlash.classList.add("on");
    this.flashT = 0.07;
  }

  kill(name: string) {
    const row = document.createElement("div");
    row.className = "kf";
    row.innerHTML = `YOU <b>KIA</b> ${name.toUpperCase()}`;
    this.killfeed.prepend(row);
    while (this.killfeed.children.length > 5) this.killfeed.lastChild?.remove();
    setTimeout(() => row.remove(), 4200);
  }

  announce(text: string) {
    this.toast.textContent = text;
    this.toast.classList.add("show");
    setTimeout(() => this.toast.classList.remove("show"), 2200);
  }

  damageDir(angle: number | false) {
    for (const k of Object.keys(this.dmg)) this.dmg[k].style.opacity = "0";
    if (angle === false) return;
    let a = angle;
    while (a > Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    const deg = (a * 180) / Math.PI;
    if (deg > -45 && deg <= 45) this.dmg.n.style.opacity = "1";
    else if (deg > 45 && deg <= 135) this.dmg.w.style.opacity = "1";
    else if (deg < -45 && deg >= -135) this.dmg.e.style.opacity = "1";
    else this.dmg.s.style.opacity = "1";
    setTimeout(() => {
      for (const k of Object.keys(this.dmg)) this.dmg[k].style.opacity = "0";
    }, 420);
  }

  update(dt: number, player: Player, arsenal: Arsenal, mission: Mission, level: LevelData, director: Director) {
    this.hmT -= dt;
    this.flashT -= dt;
    if (this.hmT <= 0) this.hitmarker.classList.remove("on");
    if (this.flashT <= 0) this.hitFlash.classList.remove("on");

    const yaw = player.yaw;
    const deg = ((-yaw * 180) / Math.PI + 36000) % 360;
    this.heading.textContent = String(Math.round(deg)).padStart(3, "0");
    const px = ((deg / 360) * 93 * 4) % 93;
    this.tape.style.transform = `translateX(${-px}px)`;

    const o = mission.objective();
    this.obj.textContent = o.main;
    const n = director.living().length;
    this.objSub.textContent = `${o.sub}  ·  ${n} MARKED`;

    const w = arsenal.cur;
    this.mag.textContent = String(w.mag);
    this.res.textContent = String(w.res);
    this.wname.textContent = w.def.name;
    this.mode.textContent = `${w.def.mode} · ${w.def.cal}`;
    this.hp.textContent = String(Math.ceil(player.health));
    this.hpFill.style.width = `${Math.max(0, player.health)}%`;
    this.arFill.style.width = `${Math.max(0, player.armor * 2)}%`;
    this.stance.textContent = player.sliding ? "SLIDE" : player.crouching ? "CROUCH" : player.sprinting ? "SPRINT" : "STAND";

    const spread = (player.ads ? 2 : 8) + Math.hypot(player.mover.vel.x, player.mover.vel.z) * 1.4;
    this.cross.style.setProperty("--sp", `${spread}px`);
    this.cross.classList.toggle("ads", player.ads);
    this.cross.classList.add("spread");

    const hint = mission.interactHint(player);
    this.interact.classList.toggle("hidden", !hint);
    if (hint) this.interactLabel.textContent = hint;

    this.drawMap(player, level, director);
    this.drawThreats(player, director);
  }

  private drawThreats(player: Player, director: Director) {
    const living = director.living();
    while (this.threats.children.length < living.length) {
      const d = document.createElement("div");
      d.className = "th";
      d.innerHTML = "<span>0m</span>";
      this.threats.appendChild(d);
    }
    const w = window.innerWidth;
    const h = window.innerHeight;
    const pad = 22;
    const eye = player.camera.position;
    living.forEach((e, i) => {
      const el = this.threats.children[i] as HTMLElement;
      const dist = Math.hypot(e.pos.x - player.mover.pos.x, e.pos.z - player.mover.pos.z);
      const dir = this.proj.set(e.pos.x - eye.x, 1.7 - eye.y, e.pos.z - eye.z);
      const len = dir.length() || 1;
      dir.multiplyScalar(1 / len);
      const wall = hitscan(eye, dir, len - 0.35, director.colliders);
      this.proj.set(e.pos.x, 1.9, e.pos.z).project(player.camera);
      const inFront = this.proj.z < 1;
      if (wall || !inFront) {
        el.style.display = "none";
        return;
      }
      const x = THREE.MathUtils.clamp((this.proj.x * 0.5 + 0.5) * w, pad, w - pad);
      const y = THREE.MathUtils.clamp((-this.proj.y * 0.5 + 0.5) * h, pad, h - pad);
      el.style.display = "block";
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.querySelector("span")!.textContent = `${Math.round(dist)}m`;
    });
    for (let i = living.length; i < this.threats.children.length; i++) {
      (this.threats.children[i] as HTMLElement).style.display = "none";
    }
  }

  private drawMap(player: Player, level: LevelData, director: Director) {
    const ctx = this.mapc.getContext("2d")!;
    const w = this.mapc.width;
    const h = this.mapc.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgba(8,10,12,0.2)";
    ctx.fillRect(0, 0, w, h);
    const scale = 1.55;
    const cx = player.mover.pos.x;
    const cz = player.mover.pos.z;
    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(player.yaw);
    ctx.translate(-cx * scale, -cz * scale);
    ctx.fillStyle = "rgba(232,226,214,0.18)";
    for (const b of level.minimap) {
      if (b.kind === "water" || b.kind === "asphalt" || b.kind === "puddle") continue;
      ctx.fillRect((b.x - b.w / 2) * scale, (b.z - b.d / 2) * scale, b.w * scale, b.d * scale);
    }
    ctx.restore();
    ctx.fillStyle = "#ff4d2e";
    for (const e of director.enemies) {
      if (e.state === "dead") continue;
      const dx = (e.pos.x - cx) * scale;
      const dz = (e.pos.z - cz) * scale;
      const rx = dx * Math.cos(-player.yaw) - dz * Math.sin(-player.yaw);
      const rz = dx * Math.sin(-player.yaw) + dz * Math.cos(-player.yaw);
      if (Math.abs(rx) < w / 2 && Math.abs(rz) < h / 2) {
        ctx.fillRect(w / 2 + rx - 2, h / 2 + rz - 2, 4, 4);
      }
    }
    ctx.fillStyle = "#e8e2d6";
    ctx.beginPath();
    ctx.moveTo(w / 2, h / 2 - 7);
    ctx.lineTo(w / 2 + 5, h / 2 + 6);
    ctx.lineTo(w / 2 - 5, h / 2 + 6);
    ctx.closePath();
    ctx.fill();
  }
}
